import {ChangeDetectionStrategy, Component, computed, input, output} from '@angular/core';
import {DatePipe} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {groupDocuments, parseTourId, Tour} from "../interfaces/tour.model";

type TourStatus = 'depot' | 'onTheWay' | 'delivered';

const STATUS_LABEL: Record<TourStatus, string> = {
  depot: 'Im Vertriebszentrum',
  onTheWay: 'Auf dem Weg',
  delivered: 'Geliefert',
};

@Component({
  selector: 'app-tour-card',
  imports: [DatePipe, MatIconModule, MatButtonModule],
  templateUrl: './tour-card.component.html',
  styleUrl: './tour-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {'[class.highlight]': 'highlight()'},
})
export class TourCardComponent {
  readonly tour = input.required<Tour>();
  readonly daysBack = input(0);
  readonly highlight = input(false);
  readonly updatedAt = input<Date | null>(null);

  readonly download = output<number>();
  readonly details = output<Tour>();

  readonly status = computed<TourStatus>(() => {
    const t = this.tour();
    if (t.delivered_at !== null) return 'delivered';
    return t.expected_time_of_arrival_max !== null ? 'onTheWay' : 'depot';
  });
  readonly statusLabel = computed(() => STATUS_LABEL[this.status()]);

  private readonly parsed = computed(() => parseTourId(this.tour().tour_id, this.daysBack()));
  readonly time = computed(() => this.parsed().time);
  readonly suffix = computed(() => this.parsed().suffix);
  readonly date = computed(() => this.parsed().date);

  readonly documents = computed(() => groupDocuments(this.tour().package));
  readonly packages = computed(() => this.tour().package.length);
  readonly positions = computed(() => this.documents().reduce((sum, d) => sum + d.lines.length, 0));

  readonly arrival = computed(() => {
    const t = this.tour();
    return t.delivered_at ?? t.expected_time_of_arrival_max ?? null;
  });

  readonly delayClass = computed(() => {
    const d = this.tour().delay_in_minutes;
    if (d === null) return '';
    if (d > 30) return 'text-danger';
    return d >= 10 ? 'text-alert' : 'text-success';
  });

  readonly delayText = computed(() => {
    const t = this.tour();
    const d = t.delay_in_minutes;
    if (d === null) return 'derzeit keine Information verfügbar';
    if (d >= 10 && d < 30) return 'leichte Verspätung';
    return t.delay_information ?? '';
  });
}
